import React from 'react'
import {useIntl} from 'react-intl'
import {
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
} from '../core/QueryResponseProvider'
import {Pagination} from '../../../../../_dashboard/helpers/crud-helper/pagination/PaginationComponent'
import {Newsletter} from '../core/_models'

type SortableFilters = {
  sort?: string
  order?: 'asc' | 'desc'
}

interface NewslettersTableProps {
  setFilters: React.Dispatch<React.SetStateAction<any>>
  filters: SortableFilters
  onEdit: (row: Newsletter) => void
  onDelete: (row: Newsletter) => void
  onToggleSubscribe: (row: Newsletter) => void
  busyId: string | null
}

const NewslettersTable: React.FC<NewslettersTableProps> = ({
  setFilters,
  filters,
  onEdit,
  onDelete,
  onToggleSubscribe,
  busyId,
}) => {
  const intl = useIntl()
  const {data} = useQueryResponseData()
  const pagination = useQueryResponsePagination()
  const isLoading = useQueryResponseLoading()

  const toggleSort = (field: string) => {
    setFilters((prev: SortableFilters & Record<string, unknown>) => {
      if (prev.sort !== field) {
        return {...prev, sort: field, order: 'asc', page: 0}
      }
      if (prev.order === 'asc') {
        return {...prev, order: 'desc', page: 0}
      }
      const {sort, order, ...rest} = prev
      return {...rest, page: 0}
    })
  }

  const headerClass = (field: string) => {
    if (filters.sort !== field) return 'cursor-pointer'
    return `cursor-pointer table-sort-${filters.order || 'asc'}`
  }

  return (
    <div className='card-body py-3'>
      <div className='table-responsive mb-4'>
        <table className='table align-middle gs-0 gy-4'>
          <thead>
            <tr className='fw-bold text-muted bg-light'>
              <th className={`min-w-200px ${headerClass('email')}`} onClick={() => toggleSort('email')}>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.EMAIL'})}
              </th>
              <th className={`min-w-120px ${headerClass('name')}`} onClick={() => toggleSort('name')}>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.NAME'})}
              </th>
              <th className={`min-w-120px ${headerClass('isSubscribed')}`} onClick={() => toggleSort('isSubscribed')}>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.SUBSCRIBED'})}
              </th>
              <th className='min-w-100px'>{intl.formatMessage({id: 'NEWSLETTER.TABLE.GDPR'})}</th>
              <th className='min-w-160px'>{intl.formatMessage({id: 'NEWSLETTER.TABLE.CONSENT_DATE'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'NEWSLETTER.TABLE.SOURCE'})}</th>
              <th className={`min-w-160px ${headerClass('createdAt')}`} onClick={() => toggleSort('createdAt')}>
                {intl.formatMessage({id: 'NEWSLETTER.TABLE.DATE'})}
              </th>
              <th className='min-w-200px text-end'>{intl.formatMessage({id: 'NEWSLETTER.TABLE.ACTIONS'})}</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((newsletter: Newsletter) => (
              <tr key={newsletter._id}>
                <td>
                  <span className='text-gray-800 fw-bold mb-1 fs-6'>{newsletter.email}</span>
                </td>
                <td>
                  <span className='text-gray-700 fs-7'>{newsletter.name || '—'}</span>
                </td>
                <td>
                  {newsletter.isSubscribed ? (
                    <span className='badge badge-light-success'>
                      {intl.formatMessage({id: 'NEWSLETTER.STATUS.SUBSCRIBED'})}
                    </span>
                  ) : (
                    <span className='badge badge-light-danger'>
                      {intl.formatMessage({id: 'NEWSLETTER.STATUS.UNSUBSCRIBED'})}
                    </span>
                  )}
                </td>
                <td>
                  {newsletter.gdprConsent ? (
                    <span className='badge badge-light-primary'>
                      {intl.formatMessage({id: 'NEWSLETTER.GENERAL.YES'})}
                    </span>
                  ) : (
                    <span className='badge badge-light'>
                      {intl.formatMessage({id: 'NEWSLETTER.GENERAL.NO'})}
                    </span>
                  )}
                </td>
                <td>
                  <span className='text-gray-700 fs-8'>
                    {newsletter.gdprConsentDate
                      ? new Date(newsletter.gdprConsentDate).toLocaleString()
                      : '—'}
                  </span>
                </td>
                <td>
                  <span className='text-gray-700 fs-8'>{newsletter.source || '—'}</span>
                </td>
                <td>
                  <span className='text-gray-800 fs-8'>
                    {newsletter.createdAt ? new Date(newsletter.createdAt).toLocaleString() : '—'}
                  </span>
                </td>
                <td className='text-end'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary me-1'
                    disabled={busyId === newsletter._id}
                    onClick={() => onEdit(newsletter)}
                  >
                    {intl.formatMessage({id: 'NEWSLETTER.GENERAL.EDIT'})}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light me-1'
                    disabled={busyId === newsletter._id}
                    onClick={() => onToggleSubscribe(newsletter)}
                  >
                    {newsletter.isSubscribed
                      ? intl.formatMessage({id: 'NEWSLETTER.ACTION.UNSUBSCRIBE'})
                      : intl.formatMessage({id: 'NEWSLETTER.ACTION.RESUBSCRIBE'})}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger'
                    disabled={busyId === newsletter._id}
                    onClick={() => onDelete(newsletter)}
                  >
                    {intl.formatMessage({id: 'NEWSLETTER.GENERAL.DELETE'})}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && (
        <Pagination pagination={pagination} isLoading={isLoading} updateState={setFilters} />
      )}
    </div>
  )
}

export {NewslettersTable}
